import "./account-management.module.css";

import { useState, useCallback, useEffect } from "react";
import { ButtonGroup } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { cloneDeep } from "lodash";
import { filterData, SearchType } from "filter-data";

// Slice
import { uiActions } from "stores/slice/ui";
import { selectOrgLevel } from "stores/slice/pageContext";

// Component
import Input from "components/Input";
import Button from "components/Button";
import Breadcrumbs from "components/Breadcrumb";
import DropdownWithAdvancedSearch from "components/DropdownWithAdvancedSearch";
import MessageBoxGroup from "components/MessageBoxGroup";
import { getPageLimit, sliceDataByPage, sortOrderType, renderTableHeader } from "components/PaginationContainer";
import AccountManagementTable from "./components/AccountManagementTable";
import AccountManagementpagination from "./components/AccountManagementpagination";

// Modal
import DeleteModal from "./modals/DeleteModal";
import InviteOrEditUserModal from "./modals/InviteOrEditUserModal";
import ResendVerificationEmailModal from "./modals/ResendVerificationEmailModal";

// Const
import { organizationType } from "const/nuclias/organization";
import { reloadState } from "const/nuclias/system";
import { defaultPathList, defaultMessages, defaultModalStatus, defaultSearch, searchType } from "./const";

// Hook
import { useSortableTable } from "hooks/useSortableTable";
import useStickyHeader from "hooks/useStickyHeader";

// Controller
import controller from "./controller";

const AccountManagement = () => {

  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { tableRef, stickyStyle } = useStickyHeader();

  // Slice
  const { user: { user } } = useSelector((store) => store);
  const isOrgLevel = useSelector(selectOrgLevel) === organizationType.org ? true : false;
  const pageContext = useSelector(state => state.pageContext);
  const { reloadStatus } = useSelector(state => state.fetchStatus);

  // Variable
  let selectedOrg = controller.getSelectedOrg(isOrgLevel, pageContext);

  // State
  const [isChanginOrg, setIsChanginOrg] = useState(false);
  const [messages, setMessages] = useState(cloneDeep(defaultMessages));
  const [modalStatus, setModalStatus] = useState(cloneDeep(defaultModalStatus));
  const [selectedPage, setSelectedPage] = useState(1);
  const [selectedPageLimit, setSelectedPageLimit] = useState(getPageLimit().default);
  const [originAccountList, setOrigindAccountList] = useState([]);
  const [filteredAccountList, setFilteredAccountList] = useState([]);
  const [currentAccountList, setcurrentAccountList] = useState([]);
  const [tableData, handleSorting] = useSortableTable(filteredAccountList, controller.getTableColumn(selectedOrg.orgType));
  const [sortField, setSortField] = useState('');
  const [order, setOrder] = useState(sortOrderType.asc);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [search, setSearch] = useState(cloneDeep(defaultSearch));

  // Method
  const init = useCallback(async () => {
    cleanSearch();

    try {
      dispatch(uiActions.showSpinner());
      const response = await controller.getAccountListData(selectedOrg.id);

      if (response.error) {
        throw response.error;
      }

      const accountList = controller.parseRawData(response.data, selectedOrg, pageContext.orgTreeMap);
      setOrigindAccountList(accountList);
      setFilteredAccountList(accountList);
      setcurrentAccountList(sliceDataByPage(tableData, selectedPage, selectedPageLimit));
      setSelectedPage(1);
      setSelectedPageLimit(getPageLimit().default);
      dispatch(uiActions.hideSpinner());
    } catch (error) {
      setMessages({
        ...cloneDeep(defaultMessages),
        error: error.code
      });
      dispatch(uiActions.hideSpinner());
    }
  }, [pageContext.orgTreeMap, selectedOrg]);

  const changeModalStatus = useCallback((type, value) => {
    setModalStatus({ ...modalStatus, [type]: { status: value } });
  }, []);

  const cleanSearch = () => {
    setSearch(cloneDeep(defaultSearch));
    setFilteredAccountList(originAccountList);
  }

  const handleSearch = (field, value) => {
    const udpatedSearch = cloneDeep(defaultSearch);

    switch (field) {
      case searchType.fuzzy.value:
        udpatedSearch.fuzzy = value;
        break;
      case searchType.name.value:
        udpatedSearch.fuzzy = null;
        udpatedSearch.name = value;
        udpatedSearch.email = search.email;
        udpatedSearch.role = search.role;
        break
      case searchType.email.value:
        udpatedSearch.fuzzy = null;
        udpatedSearch.name = search.name;
        udpatedSearch.email = value;
        udpatedSearch.role = search.role;
        break
      case searchType.role.value:
        udpatedSearch.fuzzy = null;
        udpatedSearch.name = search.name;
        udpatedSearch.email = search.email;
        udpatedSearch.role = value;
        break
      default:
        return;
    }

    setSearch(udpatedSearch);
    if (field === searchType.fuzzy.value) {
      const searchConditionList = [
        {
          key: [
            'name', 'email',
            'accessPrivilegeType', 'accessPrivilegeLabel', 'accessPrivilegeTLabel',
            'accessLevelLabel', 'accessLevelOwner',
            'statusLabel', 'statusTLabel',
            'lastAccessTime'
          ],
          value: udpatedSearch.fuzzy,
          type: SearchType.LK,
        }
      ];

      const fuzzySearchResult = filterData(originAccountList, searchConditionList);
      setFilteredAccountList(fuzzySearchResult);
      setSelectedPage(1);
    } else {
      const searchConditionList = [];

      searchConditionList.push({
        key: ['name'],
        value: udpatedSearch.name,
        type: SearchType.LK
      });
      searchConditionList.push({
        key: ['email'],
        value: udpatedSearch.email,
        type: SearchType.LK
      });
      searchConditionList.push({
        key: ['accessPrivilegeType', 'accessPrivilegeLabel', 'accessPrivilegeTLabel'],
        value: udpatedSearch.role,
        type: SearchType.LK
      });

      const fuzzySearchResult = filterData(originAccountList, searchConditionList);
      setFilteredAccountList(fuzzySearchResult);
      setSelectedPage(1);
    }
  }

  const getSearchKeyword = (field) => {
    if (field === searchType.fuzzy.value && search.fuzzy) {
      return search.fuzzy;
    }

    if (field === searchType.fuzzy.value && !search.fuzzy) {
      let keywordList = [];

      if (search.name) {
        const label = t(searchType.name.label);
        keywordList.push(`(${label}: ${search.name})`);
      }

      if (search.email) {
        const label = t(searchType.email.label);
        keywordList.push(`(${label}: ${search.email})`);
      }

      if (search.role) {
        const label = t(searchType.role.label);
        keywordList.push(`(${label}: ${search.role})`);
      }

      return keywordList.join(` ${t('19bf32b872')} `);
    }

    if (field === searchType.name.value) {
      return search.name;
    }

    if (field === searchType.email.value) {
      return search.email;
    }

    if (field === searchType.role.value) {
      return search.role;
    }
  }

  // Side effect
  useEffect(() => {
    if (reloadStatus.data?.reloadState === reloadState.doing) {
      setIsChanginOrg(true);
    } else {
      setIsChanginOrg(false);
    }
  }, [reloadStatus]);

  useEffect(() => {
    init();
  }, [init, pageContext]);

  useEffect(() => {
    setcurrentAccountList(sliceDataByPage(tableData, selectedPage, selectedPageLimit));
  }, [selectedPage, selectedPageLimit, tableData]);

  return (
    <>
      <Breadcrumbs pathList={defaultPathList} />

      {/* Account management */}
      <div className="layout-container layout-container--column layout-container--fluid">
        <MessageBoxGroup
          messages={messages}
          ErrorMessagesType='es'
          SuccessMessagesType='i18n'
          WarningMessagesType='i18n'
          onClose={() => setMessages(cloneDeep(defaultMessages))}
        />

        {!isChanginOrg && (
          <>
            <div className="d-flex justify-content-between">
              <ButtonGroup>
                <Button
                  label="440a99b2ea"
                  onClick={() => {
                    setSelectedAccount(null);
                    changeModalStatus('inviteAndEdit', true);
                  }}
                />
                <Button
                  label="f2a6c498fb"
                  disabled={!controller.getDeleteButtonState(currentAccountList)}
                  onClick={() => changeModalStatus('delete', true)}
                />
              </ButtonGroup>

              <DropdownWithAdvancedSearch
                value={getSearchKeyword('fuzzy')}
                alignEnd={true}
                dataBsToggleOnButton={true}
                dropdownMenuStyle={{ minWidth: 371 }}
                buttonOnClick={cleanSearch}
                onChange={e => handleSearch(searchType.fuzzy.value, e.target.value.trim())}
                onClick={cleanSearch}
              >
                <li>
                  <div className="form-title">{t('18697449d7')}</div>
                  <Input
                    type="text"
                    autoComplete="name"
                    placeholder='e202f7bdde'
                    value={getSearchKeyword('name')}
                    onChange={e => handleSearch(searchType.name.value, e.target.value.trim())}
                  />
                </li>
                <li>
                  <div className="form-title">{t('ce8ae9da5b')}</div>
                  <Input
                    type="text"
                    placeholder='abc@mail.com'
                    value={getSearchKeyword('email')}
                    onChange={e => handleSearch(searchType.email.value, e.target.value.trim())}
                  />
                </li>
                <li>
                  <div className="form-title">{t('727b677950')}</div>
                  <Input
                    type="text"
                    placeholder='03406a631b'
                    value={getSearchKeyword('role')}
                    onChange={e => handleSearch(searchType.role.value, e.target.value.trim())}
                  />
                </li>

              </DropdownWithAdvancedSearch>
            </div>

            <AccountManagementTable
              modalStatus={modalStatus}
              changeModalStatus={changeModalStatus}
              tableRef={tableRef}
              stickyStyle={stickyStyle}
              tableHeader={() => renderTableHeader(
                controller.getTableColumn(selectedOrg.orgType),
                order, setOrder, sortField, setSortField, handleSorting
              )}
              currentAccountList={currentAccountList}
              selectedPage={selectedPage}
              selectedPageLimit={selectedPageLimit}
              checkedAllState={() => controller.getCheckAllState(currentAccountList)}
              toggleCheckedAll={() => setcurrentAccountList(controller.toggleCheckedAll(currentAccountList))}
              toggleChecked={index => setcurrentAccountList(controller.toggleChecked(currentAccountList, index))}
              selectAccountCallback={(account) => setSelectedAccount(account)}
            />

            <AccountManagementpagination
              filteredAccountList={filteredAccountList}
              selectedPage={selectedPage}
              setSelectedPage={setSelectedPage}
              setSelectedPageLimit={setSelectedPageLimit}
            />
          </>
        )}

      </div>

      <DeleteModal
        modalStatus={modalStatus}
        user={user}
        currentAccountList={currentAccountList}
        changeModalStatus={changeModalStatus}
        successCallback={() => init()}
      />

      <InviteOrEditUserModal
        modalStatus={modalStatus}
        changeModalStatus={changeModalStatus}
        user={user}
        orgTreeMap={pageContext.orgTreeMap}
        selectedOrg={selectedOrg}
        selectedSiteTag={pageContext.selectedStoreTag}
        selectedSite={pageContext.selectedStore}
        selectedAccount={selectedAccount}
        successCallback={() => init()}
      />

      <ResendVerificationEmailModal
        modalStatus={modalStatus}
        changeModalStatus={changeModalStatus}
        selectedAccount={selectedAccount}
        successCallback={() => init()}
      />
    </>
  );
};

export default AccountManagement;
